import { Handle, Position } from '@xyflow/react';
import { cn } from '@/lib/utils';

export const SupplyChainNode = ({ data }) => {
  const { label, icon: Icon, capacity, status, alerts, selected } = data;
  const hasAlerts = alerts && alerts.length > 0;
  const isBottleneck = capacity > 90;

  return (
    <div
      title={`${label}: ${capacity}% utilization${hasAlerts ? ` \u00b7 ${alerts.length} alert${alerts.length > 1 ? 's' : ''}` : ''}`}
      className={cn(
        'bg-white/85 backdrop-blur-sm border-2 rounded-2xl p-4 w-[220px] h-[160px] transition-all duration-300',
        'shadow-elevated hover:shadow-lg cursor-pointer group relative',
        status === 'ok' && 'border-green-200 hover:border-green-300',
        status === 'warning' && 'border-yellow-300 hover:border-yellow-400',
        status === 'error' && 'border-red-300 hover:border-red-400',
        hasAlerts && 'ring-2 ring-orange-300/50 ring-offset-1',
        isBottleneck && 'border-dashed border-red-400',
        selected && 'ring-2 ring-chart-1/60 ring-offset-2',
      )}
    >
      <Handle type="target" position={Position.Left} className="w-3 h-3 !bg-chart-1 !border-2 !border-white" />

      {/* Status indicator with pulse */}
      <div className={cn(
        'absolute top-3 right-3 w-2.5 h-2.5 rounded-full',
        status === 'ok' && 'bg-green-500 status-pulse-green',
        status === 'warning' && 'bg-yellow-500 status-pulse-yellow',
        status === 'error' && 'bg-red-500 status-pulse-red',
      )} />

      {/* Bottleneck indicator */}
      {isBottleneck && (
        <div className="absolute -top-2 left-1/2 -translate-x-1/2 bg-red-500 text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full leading-none">
          {data.bottleneckLabel || 'Úzké místo'}
        </div>
      )}

      <div className="flex flex-col items-center gap-2 h-full justify-center">
        <div className={cn(
          'w-11 h-11 rounded-xl flex items-center justify-center transition-colors duration-200',
          'bg-gradient-to-br from-chart-1/10 to-chart-1/20 group-hover:from-chart-1/20 group-hover:to-chart-1/30',
        )}>
          <Icon className="h-5 w-5 text-chart-1" />
        </div>
        <div className="text-base font-bold text-center">{label}</div>

        {/* Metrics row */}
        <div className="w-full flex items-center justify-between text-[11px] text-muted-foreground">
          <span style={{ fontVariantNumeric: 'tabular-nums' }}>{data.metrics?.currentOutput || 0} t/h</span>
          <span className="font-bold text-foreground" style={{ fontVariantNumeric: 'tabular-nums' }}>{capacity}%</span>
        </div>

        {/* Capacity bar */}
        <div className="w-full h-2.5 bg-gray-100 rounded-full overflow-hidden">
          <div
            className={cn(
              'h-full rounded-full transition-all duration-1000',
              capacity > 85 && 'bg-gradient-to-r from-red-400 to-red-500',
              capacity > 60 && capacity <= 85 && 'bg-gradient-to-r from-yellow-400 to-yellow-500',
              capacity <= 60 && 'bg-gradient-to-r from-green-400 to-green-500',
            )}
            style={{ width: `${Math.min(capacity, 100)}%` }}
          />
        </div>

        {/* Alert count */}
        {hasAlerts && (
          <div className="text-[9px] font-semibold text-orange-600 bg-orange-50 px-1.5 py-0.5 rounded-full">
            {alerts.length} alert{alerts.length > 1 ? 's' : ''}
          </div>
        )}
      </div>

      <Handle type="source" position={Position.Right} className="w-3 h-3 !bg-chart-1 !border-2 !border-white" />
    </div>
  );
};
